import React, { useState, useEffect } from 'react';
import { X, User, Check, Sparkles, ArrowRight, ChevronDown } from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';
const AvatarOnboardingDialog = ({ 
  isOpen, 
  onClose, 
  onAvatarSelect, 
  onSkip,
  API_URL,
  userName = "Welcome!",
  isOnboarding = true,
  currentAvatarUrl = null
}) => {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [step, setStep] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const { colors } = useTheme(); 

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch avatars from the avatar_library table
  const fetchAvatars = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/avatars`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        setAvatars(result.data);
        // Pre-select the first avatar
        if (result.data.length > 0) {
          setSelectedAvatar(result.data[0].url);
        }
      } else {
        console.error('Failed to fetch avatars:', result.error);
        setAvatars([]);
      }
    } catch (error) {
      console.error('Error fetching avatars:', error);
      setAvatars([]);
    } finally {
      setLoading(false);
    }
  };

  // Update user's avatar
  const handleAvatarUpdate = async (avatarUrl) => {
    try {
      setUpdating(true);
      const response = await fetch(`${API_URL}/api/profile/avatar`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_avatar: avatarUrl
        })
      });

      const result = await response.json();
      if (result.success) {
        onAvatarSelect(avatarUrl);
        onClose();
      } else {
        console.error('Error updating avatar:', result.error);
        onClose();
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      onClose();
    } finally {
      setUpdating(false);
    }
  };

  // Fetch avatars when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (isOnboarding && step === 2) {
        fetchAvatars();
      } else if (!isOnboarding) {
        setStep(2);
        fetchAvatars();
      }
    }
  }, [isOpen, step, isOnboarding]);

  // Reset dialog state when it opens
  useEffect(() => {
    if (isOpen) {
      if (isOnboarding) {
        setStep(1);
      } else {
        setStep(2);
      }
      setSelectedAvatar(currentAvatarUrl || null);
    }
  }, [isOpen, isOnboarding, currentAvatarUrl]);

  if (!isOpen) return null;

  // Handle close attempts - only allow if not onboarding
  const handleCloseAttempt = () => {
    if (!isOnboarding && !updating) {
      onClose();
    }
  };

  // Handle backdrop click - only allow closing if not onboarding
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isOnboarding && !updating) {
      onClose();
    }
  };

  const handleContinue = () => {
    if (step === 1) {
      setStep(2);
    } else {
      if (selectedAvatar) {
        handleAvatarUpdate(selectedAvatar);
      } else {
        onClose();
      }
    }
  };

  const handleSkip = async () => {
    if (isOnboarding && avatars.length > 0) {
      const defaultAvatar = avatars[0].url;
      
      try {
        setUpdating(true);
        const response = await fetch(`${API_URL}/api/profile/avatar`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_avatar: defaultAvatar
          })
        });

        const result = await response.json();
        if (result.success) {
          onAvatarSelect(defaultAvatar);
        }
      } catch (error) {
        console.error('Error setting default avatar:', error);
      } finally {
        setUpdating(false);
      }
    }
    
    if (onSkip) {
      onSkip();
    }
    
    onClose();
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? '10px' : '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        borderRadius: '24px',
        border: '1px solid #333',
        width: '90%',
        maxWidth: isMobile ? '380px' : '600px',
        maxHeight: isMobile ? '85vh' : '90vh',
        minHeight: isMobile ? '500px' : '600px',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Close button - Only show when not onboarding */}
        {!isOnboarding && (
          <button
            onClick={handleCloseAttempt}
            disabled={updating}
            style={{
              position: 'absolute',
              top: isMobile ? '12px' : '20px',
              right: isMobile ? '12px' : '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#888',
              cursor: updating ? 'not-allowed' : 'pointer',
              padding: isMobile ? '12px' : '8px',
              borderRadius: '50%',
              transition: 'all 0.3s ease',
              opacity: updating ? 0.5 : 1,
              zIndex: 10,
              minWidth: isMobile ? '44px' : 'auto',
              minHeight: isMobile ? '44px' : 'auto'
            }}
            onMouseEnter={(e) => {
              if (!updating) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.color = '#fff';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = '#888';
            }}
          >
            <X size={20} />
          </button>
        )}

        {/* Visual indicator for required onboarding */}
        {isOnboarding && (
          <div style={{
            position: 'absolute',
            top: isMobile ? '12px' : '20px',
            right: isMobile ? '12px' : '20px',
            background: 'rgba(251, 191, 36, 0.2)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            color: '#fbbf24',
            padding: isMobile ? '6px 12px' : '4px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            zIndex: 10
          }}>
            Required
          </div>
        )}

        {step === 1 ? (
          // Welcome Step
          <div style={{
            padding: isMobile ? '32px 24px' : '48px 40px',
            textAlign: 'center',
            background: 'radial-gradient(circle at center, rgba(0, 212, 170, 0.1) 0%, transparent 70%)'
          }}>
            {/* Welcome Animation */}
            <div style={{
              animation: 'float 3s ease-in-out infinite',
              marginBottom: '24px'
            }}>
              <div style={{
                width: isMobile ? '60px' : '80px',
                height: isMobile ? '60px' : '80px',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 12px 30px rgba(16, 185, 129, 0.4)',
                animation: 'glow 2s ease-in-out infinite'
              }}>
                <Sparkles size={isMobile ? 30 : 40} color="white" />
              </div>
            </div>

            <h2 style={{
              margin: '0 0 16px 0',
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #e5e5e5 0%, #ffffff 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Welcome to HighMind, {userName}!
            </h2>

            <p style={{
              fontSize: isMobile ? '16px' : '18px',
              color: '#bbb',
              marginBottom: '32px',
              lineHeight: '1.6',
              maxWidth: '400px',
              margin: '0 auto 32px auto'
            }}>
              Your cannabis community journey starts here. Let's personalize your experience!
            </p>

            {/* Benefits Preview */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: isMobile ? '20px' : '24px',
              marginBottom: '32px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{
                margin: '0 0 16px 0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#00d4aa'
              }}>
                What's Next?
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)',
                gap: '12px',
                fontSize: '14px',
                color: '#bbb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>🎨</span>
                  Choose your avatar
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>💬</span>
                  Join discussions
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>⭐</span>
                  Share reviews
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>🏆</span>
                  Earn achievements
                </div>
              </div>
            </div>

            <button
              onClick={handleContinue}
              style={{
                padding: isMobile ? '16px 24px' : '16px 32px',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                margin: '0 auto',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                minHeight: isMobile ? '48px' : 'auto',
                minWidth: isMobile ? '200px' : 'auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.3)';
              }}
            >
              Let's Get Started
              <ArrowRight size={20} />
            </button>
          </div>
        ) : (
          // Avatar Selection Step
          <>
            {/* Header */}
            <div style={{
              padding: isMobile ? '24px 20px 16px 20px' : '32px 32px 24px 32px',
              borderBottom: `1px solid ${colors.border}`,
              textAlign: 'center'
            }}>
              <div style={{
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                animation: 'pulse 2s infinite'
              }}>
                <User size={isMobile ? 20 : 24} color="white" />
              </div>
              
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '700',
                color: '#e5e5e5'
              }}>
                {isOnboarding ? 'Choose Your Avatar' : 'Select Avatar'}
              </h2>
              <p style={{
                margin: 0,
                color: '#888',
                fontSize: isMobile ? '13px' : '14px'
              }}>
                {isOnboarding 
                  ? 'Pick an avatar that represents you in the community' 
                  : 'Choose from our collection of avatars'
                }
              </p>
            </div>

            {/* Content */}
            <div style={{
              padding: isMobile ? '20px 16px' : '24px 32px',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0
            }}>
              {loading ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px',
                  color: '#888'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #333',
                    borderTop: '3px solid #00d4aa',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '16px'
                  }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>Loading avatars...</p>
                </div>
              ) : avatars.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#888'
                }}>
                  <User size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    No avatars available
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile 
                    ? 'repeat(auto-fill, minmax(110px, 1fr))' 
                    : 'repeat(auto-fill, minmax(90px, 1fr))',
                  gap: isMobile ? '20px' : '16px'
                }}>
                  {avatars.map((avatar, index) => {
                    const isSelected = selectedAvatar === avatar.url;
                    const isCurrent = currentAvatarUrl === avatar.url;
                    
                    return (
                      <div
                        key={avatar.id}
                        onClick={() => {
                          if (!updating) {
                            setSelectedAvatar(avatar.url);
                          }
                        }}
                        style={{
                          position: 'relative',
                          cursor: updating ? 'not-allowed' : 'pointer',
                          opacity: updating ? 0.6 : 1,
                          transition: 'all 0.3s ease',
                          animation: `slideIn 0.6s ease-out ${index * 0.1}s both`,
                          minHeight: isMobile ? '120px' : '90px',
                          padding: isMobile ? '8px' : '0',
                          borderRadius: '20px',
                          background: isMobile && isSelected 
                            ? 'rgba(0, 212, 170, 0.1)' 
                            : 'transparent'
                        }}
                      >
                        <div style={{
                          position: 'relative',
                          borderRadius: '16px',
                          border: `3px solid ${isSelected ? '#00d4aa' : (isCurrent ? '#fbbf24' : '#333')}`,
                          overflow: 'hidden',
                          background: isSelected ? 'rgba(0, 212, 170, 0.2)' : (isCurrent ? 'rgba(251, 191, 36, 0.2)' : 'transparent'),
                          transition: 'all 0.3s ease',
                          transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                          boxShadow: isSelected ? '0 8px 24px rgba(0, 212, 170, 0.3)' : (isCurrent ? '0 4px 12px rgba(251, 191, 36, 0.3)' : 'none'),
                          width: isMobile ? '100px' : '90px',
                          height: isMobile ? '100px' : '90px',
                          margin: isMobile ? '0 auto' : '0'
                        }}>
                          <img
                            src={avatar.url}
                            alt={`Avatar option ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                          
                          {/* Selection indicator */}
                          {isSelected && (
                            <div style={{
                              position: 'absolute',
                              top: isMobile ? '6px' : '8px',
                              right: isMobile ? '6px' : '8px',
                              width: isMobile ? '28px' : '24px',
                              height: isMobile ? '28px' : '24px',
                              background: '#00d4aa',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              animation: 'pulse 1.5s infinite'
                            }}>
                              <Check size={isMobile ? 16 : 14} color="white" />
                            </div>
                          )}
                          
                          {/* Current indicator */}
                          {isCurrent && !isSelected && (
                            <div style={{
                              position: 'absolute',
                              bottom: '4px',
                              left: '4px',
                              right: '4px',
                              background: 'rgba(251, 191, 36, 0.9)',
                              borderRadius: '4px',
                              padding: isMobile ? '3px' : '2px',
                              textAlign: 'center'
                            }}>
                              <span style={{
                                fontSize: isMobile ? '11px' : '10px',
                                fontWeight: '600',
                                color: 'white'
                              }}>
                                Current
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: isMobile ? '20px 16px' : '24px 32px',
              borderTop: '1px solid #333',
              display: 'flex',
              gap: '12px',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: isMobile ? 'column-reverse' : 'row'
            }}>
              {/* Continue/Confirm Button */}
              <button
                onClick={handleContinue}
                disabled={updating || (step === 2 && !selectedAvatar)}
                style={{
                  padding: isMobile ? '16px 24px' : '12px 24px',
                  background: (updating || (step === 2 && !selectedAvatar))
                    ? '#333' 
                    : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                  color: (updating || (step === 2 && !selectedAvatar))
                    ? '#666' 
                    : 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: (updating || (step === 2 && !selectedAvatar))
                    ? 'not-allowed' 
                    : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  width: isMobile ? '100%' : 'auto',
                  minHeight: isMobile ? '48px' : 'auto',
                  order: isMobile ? 1 : 2
                }}
                onMouseEnter={(e) => {
                  if (!updating && !(step === 2 && !selectedAvatar)) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {updating && (
                  <div style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid transparent',
                    borderTop: '2px solid currentColor',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {step === 1 ? (
                  <>
                    Continue
                    <ArrowRight size={16} />
                  </>
                ) : updating ? (
                  'Saving...'
                ) : (
                  <>
                    <Check size={16} />
                    Complete Setup
                  </>
                )}
              </button>

              {/* Skip Button */}
              <button
                onClick={handleSkip}
                disabled={updating}
                style={{
                  padding: isMobile ? '14px 20px' : '12px 20px',
                  background: 'transparent',
                  color: '#888',
                  border: '2px solid #333',
                  borderRadius: '12px',
                  cursor: updating ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updating ? 0.5 : 1,
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto',
                  minHeight: isMobile ? '44px' : 'auto',
                  order: isMobile ? 2 : 1
                }}
                onMouseEnter={(e) => {
                  if (!updating) {
                    e.currentTarget.style.borderColor = '#666';
                    e.currentTarget.style.color = '#bbb';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#333';
                  e.currentTarget.style.color = '#888';
                }}
              >
                <ChevronDown size={16} />
                Skip for now
              </button>
            </div>
          </>
        )}

        {/* CSS Animations */}
        <style>
          {`
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            @keyframes float {
              0%, 100% {
                transform: translateY(0px);
              }
              50% {
                transform: translateY(-10px);
              }
            }
            
            @keyframes glow {
              0%, 100% {
                box-shadow: 0 12px 30px rgba(16, 185, 129, 0.4);
              }
              50% {
                box-shadow: 0 16px 40px rgba(16, 185, 129, 0.6);
              }
            }
            
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.8;
                transform: scale(1.05);
              }
            }
            
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default AvatarOnboardingDialog;